import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Copy, Check, ExternalLink } from "lucide-react";
import { PARTNER_LINK, DISCOUNT_CODE, DISCOUNT_PERCENT } from "@/lib/constants";

import heroMain from "@/assets/hero-main.webp";

const HeroSection = () => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(DISCOUNT_CODE);
    setCopied(true);
    window.lh?.trackCodeCopy({ source: "hero" });
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <section id="rabattcode" className="relative md:min-h-[75vh] md:flex md:items-center md:overflow-hidden">
      {/* Preload hero image only on desktop viewports — mobile shows it lazily below the fold */}
      <Helmet>
        <link
          rel="preload"
          as="image"
          href={heroMain}
          media="(min-width: 768px)"
          // @ts-expect-error: fetchpriority is valid HTML, not yet in React DOM typings for link
          fetchpriority="high"
        />
      </Helmet>

      {/* Full-bleed hero image — desktop only, so the mobile screenshot shows text first */}
      <figure className="hidden md:block absolute inset-0">
        <img
          src={heroMain}
          alt="heilys Rabattcode LONGEVITY – Rotlichtanwendung mit heilys Gerät"
          title="heilys Rabattcode LONGEVITY für 5 % Rabatt einlösen"
          className="h-full w-full object-cover object-center"
          loading="eager"
          fetchPriority="high"
          decoding="async"
          width={1200}
          height={800}
        />
        <figcaption className="sr-only">
          heilys Rabattcode LONGEVITY – spare 5 % auf alle heilys Rotlichtgeräte. Code im Checkout eingeben und Rabatt sichern.
        </figcaption>
        {/* Gradient overlays for text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-background/95 via-background/80 to-background/40 md:via-background/70 md:to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-background/40" />
      </figure>

      <div className="container relative py-8 md:py-24">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          {/* Left: Content */}
          <div className="max-w-xl">
            <h1 className="font-display text-2xl font-extrabold leading-tight tracking-tight text-foreground sm:text-3xl md:text-5xl">
              heilys Rabattcode LONGEVITY – {DISCOUNT_PERCENT}&nbsp;% Rotlichttherapie-Discount &amp; Cashback-Alternative
            </h1>
            <a
              href="#cashback-discount"
              className="mt-3 inline-flex items-center gap-2 rounded-full border border-primary/50 bg-primary/20 px-3.5 py-1.5 text-xs font-semibold text-primary shadow-sm transition-all hover:bg-primary/30 hover:border-primary/70 sm:text-sm"
            >
              <span className="relative flex h-2 w-2" aria-hidden="true">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75"></span>
                <span className="relative inline-flex h-2 w-2 rounded-full bg-primary"></span>
              </span>
              Discount-Code &amp; Cashback-Alternative – {DISCOUNT_PERCENT}&nbsp;% direkt im Checkout
            </a>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:mt-4 sm:text-base md:text-lg">
              Auf heilys.info findest du den aktuellen heilys Rabattcode LONGEVITY für {DISCOUNT_PERCENT}&nbsp;% Rotlichttherapie-Discount, eine Cashback-Alternative ohne Gutscheinportal sowie eine unabhängige Übersicht zum gesamten Sortiment und eine schnelle Orientierungshilfe bei der Auswahl passender Rotlichtprodukte.
            </p>

            {/* CTA area */}
            <div className="mt-6 md:mt-8">
              <div className="inline-flex items-center gap-2 sm:gap-3 rounded-xl border border-border bg-card/80 backdrop-blur-sm px-3 py-2.5 sm:px-5 sm:py-3">
                <span className="text-[10px] sm:text-xs font-medium uppercase tracking-widest text-muted-foreground">Code</span>
                <span className="font-display text-xl font-bold tracking-wider text-primary sm:text-2xl md:text-3xl">
                  {DISCOUNT_CODE}
                </span>
                <button
                  onClick={handleCopy}
                  className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 sm:px-3 sm:py-1.5 text-[11px] sm:text-xs font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                  aria-label="Rabattcode LONGEVITY in die Zwischenablage kopieren"
                >
                  {copied ? (
                    <>
                      <Check className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-success" aria-hidden="true" />
                      <span className="text-success">Kopiert</span>
                    </>
                  ) : (
                    <>
                      <Copy className="h-3 w-3 sm:h-3.5 sm:w-3.5" aria-hidden="true" />
                      Kopieren
                    </>
                  )}
                </button>
              </div>

              <div className="mt-4 sm:mt-5 flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-3">
                <a
                  href={PARTNER_LINK}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  onClick={() => window.lh?.trackAffiliate({ source: "hero" })}
                  className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground shadow-lg shadow-primary/25 transition-all hover:shadow-xl hover:shadow-primary/30 hover:brightness-110 w-full sm:w-auto"
                >
                  Zum heilys Shop
                  <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                </a>
                <span className="text-xs sm:text-sm text-muted-foreground">
                  {DISCOUNT_PERCENT}&nbsp;% Rabatt auf das gesamte Sortiment
                </span>
              </div>

              <p className="mt-3 sm:mt-4 text-[11px] text-muted-foreground">
                Unabhängige Informationsseite mit transparentem Partnerhinweis.
              </p>
            </div>
          </div>

          {/* Right: empty space where the image shows through */}
          <div className="hidden lg:block" />
        </div>

        {/* Mobile-only image block — placed after text so the initial paint shows H1/CTA */}
        <figure className="md:hidden mt-8 overflow-hidden rounded-xl border border-border">
          <img
            src={heroMain}
            alt="heilys Rabattcode LONGEVITY – Rotlichtanwendung mit heilys Gerät"
            className="h-auto w-full object-cover"
            loading="lazy"
            decoding="async"
            width={1200}
            height={800}
          />
        </figure>

        {/* Anchor navigation */}
        <nav aria-label="Schnellnavigation" className="mt-6 md:mt-10 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-muted-foreground">
          <a href="#ueber-heilys" className="hover:text-foreground transition-colors">Über heilys</a>
          <span className="text-border hidden sm:inline" aria-hidden="true">·</span>
          <a href="#produktwelten" className="hover:text-foreground transition-colors">Produktwelten</a>
          <span className="text-border hidden sm:inline" aria-hidden="true">·</span>
          <a href="#produkt-spotlight" className="hover:text-foreground transition-colors">Gen30+Vision</a>
          <span className="text-border hidden sm:inline" aria-hidden="true">·</span>
          <a href="#auswahlhilfe" className="hover:text-foreground transition-colors">Auswahlhilfe</a>
          <span className="text-border hidden sm:inline" aria-hidden="true">·</span>
          <a href="#anleitung" className="hover:text-foreground transition-colors">So funktioniert's</a>
          <span className="text-border hidden sm:inline" aria-hidden="true">·</span>
          <a href="#cashback-discount" className="hover:text-foreground transition-colors">Cashback &amp; Discount</a>
          <span className="text-border hidden sm:inline" aria-hidden="true">·</span>
          <a href="#faq" className="hover:text-foreground transition-colors">Häufige Fragen</a>
          <span className="text-border hidden sm:inline" aria-hidden="true">·</span>
          <a href="#erfahrungen" className="hover:text-foreground transition-colors">Erfahrungen</a>
        </nav>
      </div>
    </section>
  );
};

export default HeroSection;
